import React from "react";
import { Combobox, Input, Option } from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { id, utc } from "../../utils";
import {
  allSelected,
  getSelectedOptions,
  type Item,
  type ItemsState,
  type ParameterDefinition,
  type StringState,
} from "../ReportServices";
import type { ReportParamsViewStyles } from "./ReportParamsViewStyles";
import { useIncrementalOptions } from "./useIncrementalOptions";

type ReportParameterFieldProps = {
  parameter: ParameterDefinition;
  styles: ReportParamsViewStyles;
  items: ItemsState;
  selectedItem: StringState;
  areItemsLoading: Record<string, boolean>;
  comboboxQuery: string;
  setComboboxQuery: (value: string) => void;
  comboboxOpen: string;
  setComboboxOpen: (value: string) => void;
  isComboboxOpen: boolean;
  setIsComboboxOpen: (value: boolean) => void;
  setSelectedItem: (key: string, item: string) => void;
  handleOptionSelectLocal: (nameId: string, data: any, targetItems: Item[]) => void;
};

const ReportParameterField: React.FC<ReportParameterFieldProps> = ({
  parameter,
  styles,
  items,
  selectedItem,
  areItemsLoading,
  comboboxQuery,
  setComboboxQuery,
  comboboxOpen,
  setComboboxOpen,
  isComboboxOpen,
  setIsComboboxOpen,
  setSelectedItem,
  handleOptionSelectLocal,
}) => {
  const parameterName = parameter.name;
  const nameId = id(parameterName);
  const type = parameter.type;
  const itemList = items[nameId];
  const selectedValue = selectedItem[nameId];
  // While a call for this parameter is in flight its items are the previous dependency's, so the
  // field shows "Loading..." rather than a list the user can no longer choose from.
  const isItemsLoading = !!areItemsLoading[nameId];
  const isThisComboboxOpen = isComboboxOpen && comboboxOpen == nameId;
  // The query is shared between all parameter fields, so only the open one filters by it.
  const activeQuery = isThisComboboxOpen ? comboboxQuery : "";

  // Only a page of the items is rendered as options; the rest is appended as the user scrolls.
  const { visibleItems, filteredItems, listboxProps, onActiveOptionChange, disableAutoFocus } = useIncrementalOptions(
    isItemsLoading ? undefined : itemList,
    activeQuery,
    isThisComboboxOpen
  );

  // These all walk the full item list, which can hold six figures worth of items, so they are kept
  // out of the render path unless the items or the selection actually changed.
  const selectedOptions = React.useMemo(
    () => getSelectedOptions(itemList, selectedValue, activeQuery ? filteredItems : itemList),
    [itemList, selectedValue, filteredItems, activeQuery]
  );
  const isAllSelected = React.useMemo(() => allSelected(itemList, selectedValue), [itemList, selectedValue]);
  const selectedName = React.useMemo(
    () => itemList?.find((p) => p.id === selectedValue)?.name,
    [itemList, selectedValue]
  );
  const selectedNames = React.useMemo(() => {
    const selectedIds = new Set(selectedValue ? selectedValue.split(", ") : []);
    return (
      itemList
        ?.filter((p) => selectedIds.has(p.id))
        ?.map((p) => p.name)
        ?.join(", ") ?? ""
    );
  }, [itemList, selectedValue]);

  const renderParameterLabel = (controlId: string) => (
    <label htmlFor={controlId} className={styles.parameterLabel}>
      {parameterName}
      {parameter.required ? (
        <span className={styles.parameterRequiredMarker} title="Required" aria-label="required">
          *
        </span>
      ) : null}
    </label>
  );
  let content;

  if (type.startsWith("select")) {
    const displayValue = isItemsLoading ? "" : ((isThisComboboxOpen && comboboxQuery ? comboboxQuery : selectedName) ?? "");
    content = (
      <div>
        {renderParameterLabel("dd" + nameId)}
        <Combobox
          id={"dd" + nameId}
          title={displayValue}
          placeholder={isItemsLoading ? "Loading..." : (itemList?.length ?? 0) == 0 ? "No items" : "Select"}
          className={styles.parameterControl}
          size="small"
          disabled={isItemsLoading || (itemList?.length ?? 0) == 0}
          autoComplete="off"
          disableAutoFocus={disableAutoFocus}
          onOpenChange={(_, data) => {
            setComboboxQuery("");
            setComboboxOpen(nameId);
            setIsComboboxOpen(data.open);
          }}
          onChange={(ev) => {
            const query = ev.target.value.toLowerCase();
            setComboboxQuery(query);
            // Backspacing the text to empty clears the selection too, rather than letting the
            // previously selected name reappear once the query goes falsy.
            if (query === "") {
              setSelectedItem(nameId, "");
            }
          }}
          value={displayValue}
          selectedOptions={selectedOptions}
          onOptionSelect={(_, data) => setSelectedItem(nameId, data.optionValue || "")}
          onActiveOptionChange={onActiveOptionChange}
          listbox={{ style: { maxHeight: "200px", overflowY: "auto" }, ...listboxProps }}
          positioning={{
            position: "below",
            align: "start",
            pinned: true,
            autoSize: "width",
          }}
        >
          {visibleItems.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.name}
            </Option>
          ))}
        </Combobox>
      </div>
    );
  } else if (type.startsWith("multiselect")) {
    const displayValue = isItemsLoading
      ? ""
      : isThisComboboxOpen && comboboxQuery
        ? comboboxQuery
        : isAllSelected
          ? (parameter.default?.length ?? 0) > 0
            ? parameter.default
            : "All"
          : selectedNames;
    content = (
      <div>
        {renderParameterLabel("dd" + nameId)}
        <Combobox
          id={"dd" + nameId}
          title={displayValue}
          placeholder={isItemsLoading ? "Loading..." : (itemList?.length ?? 0) == 0 ? "No items" : "Select"}
          className={styles.parameterControl}
          size="small"
          disabled={isItemsLoading || (itemList?.length ?? 0) == 0}
          multiselect={true}
          autoComplete="off"
          disableAutoFocus={disableAutoFocus}
          onOpenChange={(_, data) => {
            setComboboxQuery("");
            setComboboxOpen(nameId);
            setIsComboboxOpen(data.open);
          }}
          onChange={(ev) => {
            const query = ev.target.value.toLowerCase();
            setComboboxQuery(query);
            // Backspacing the text to empty clears the selection too, rather than letting the
            // previously selected names reappear once the query goes falsy.
            if (query === "") {
              setSelectedItem(nameId, "");
            }
          }}
          value={displayValue}
          selectedOptions={selectedOptions}
          onOptionSelect={(_, data) => handleOptionSelectLocal(nameId, data, filteredItems)}
          onActiveOptionChange={onActiveOptionChange}
          listbox={{ style: { maxHeight: "200px", overflowY: "auto" }, ...listboxProps }}
          positioning={{
            position: "below",
            align: "start",
            pinned: true,
            autoSize: "width",
          }}
        >
          {!isItemsLoading && filteredItems.length > 0 ? (
            <Option key="all" value="all">
              {((parameter.default?.length ?? 0) > 0 ? parameter.default : "Select All") +
                (activeQuery ? " (filtered)" : "")}
            </Option>
          ) : null}
          {visibleItems.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.name}
            </Option>
          ))}
        </Combobox>
      </div>
    );
  } else if (type == "text" || type == "number") {
    content = (
      <div>
        {renderParameterLabel("tb" + nameId)}
        <Input
          id={"tb" + nameId}
          placeholder={"Enter " + type}
          className={styles.parameterControl}
          type={type}
          value={selectedItem[nameId] || ""}
          onChange={(_, data) => setSelectedItem(nameId, data.value)}
          size="small"
        />
      </div>
    );
  } else if (type == "date") {
    content = (
      <div>
        {renderParameterLabel("dp" + nameId)}
        <DatePicker
          id={"dp" + nameId}
          placeholder="Select"
          className={styles.parameterControl}
          size="small"
          value={selectedItem[nameId] ? new Date(selectedItem[nameId]) : null}
          allowTextInput={true}
          onSelectDate={(date: Date | null | undefined) => setSelectedItem(nameId, date ? utc(date).toISOString() : "")}
        />
      </div>
    );
  } else {
    content = <div className={styles.parameterWarning}>Unknown parameter type for {parameterName}.</div>;
  }

  return (
    <div key={parameter.name} className={styles.parameter}>
      {content}
    </div>
  );
};

export default ReportParameterField;
